import {
  FormControl,
  FormHelperText,
  InputAdornment,
  Link,
  TextField,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { theme as defaultTheme, Button } from "@mindlercare/ui-web";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import CustomModal from "components/lib/customModal";
import { useRefundDialogParam } from "hooks/useRefundDialogParam";
import { Typography } from "components/typography";
import { getPaylogs, refundPayment } from "./api";
import { Payment, PaymentLogItem, PaymentWithFormattedData } from "./types";

const RefundDialogActions = ({
  onRefund,
  onClose,
  payment,
  isRefunding,
}: {
  isRefunding: boolean;
  payment: PaymentWithFormattedData;
  onRefund: (amount?: number) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [refundAmountError, setRefundAmountError] = useState<string>();
  const [amountToBeRefundedNormalized, setAmountToBeRefundedNormalized] =
    useState(payment.payedAmountValue / 100);

  // Adds compatibility with old api
  let paymentIsPartiallyRefundable = false;
  let paymentIsRefundable =
    payment.status === "AUTHORIZATION" ||
    payment.status === "CAPTURE" ||
    payment.status === "PARTIAL_REFUND";
  // New columns are defiend => new api present
  if (payment.isPartiallyRefundable != null && payment.isRefundable != null) {
    paymentIsPartiallyRefundable = payment.isPartiallyRefundable;
    paymentIsRefundable = payment.isRefundable;
  }

  const maybeRenderRefundActions = () => {
    if (!paymentIsPartiallyRefundable) {
      return null;
    }

    return (
      <>
        <FormControl>
          <TextField
            value={amountToBeRefundedNormalized}
            type="number"
            label={t("AMOUNT_TO_REFUND")}
            onChange={(e) => {
              const newAmount = parseInt(e.target.value, 10);
              setAmountToBeRefundedNormalized(newAmount);
              if (
                newAmount <= 0 ||
                newAmount * 100 > payment.payedAmountValue ||
                Number.isNaN(newAmount)
              ) {
                setRefundAmountError(t("REFUND_INVALID_AMOUNT"));
              } else {
                setRefundAmountError(undefined);
              }
            }}
            InputProps={{
              inputProps: {
                min: 1,
              },
              endAdornment: (
                <InputAdornment position="end">
                  {payment.currency}
                </InputAdornment>
              ),
            }}
          />
          <FormHelperText error id="accountId-error">
            {refundAmountError}
            <div
              style={{
                float: "right",
                paddingLeft: 4,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Link
                href="#"
                style={{
                  textAlign: "right",
                }}
                onClick={() => {
                  setAmountToBeRefundedNormalized(
                    payment.payedAmountValue / 100,
                  );
                  setRefundAmountError(undefined);
                }}
              >
                {t("RESET")}
              </Link>
              <Typography variant="body-x-small">
                {t("AMOUNT_LEFT_AFTER_REFUND")}:{" "}
                {payment.payedAmountValue / 100 -
                  (Number.isNaN(amountToBeRefundedNormalized)
                    ? 0
                    : amountToBeRefundedNormalized)}{" "}
                {payment.currency}
              </Typography>
            </div>
          </FormHelperText>
        </FormControl>
      </>
    );
  };

  const amountToBeRefunded = amountToBeRefundedNormalized * 100;
  const isPartialRefund =
    amountToBeRefunded < payment.payedAmountValue &&
    paymentIsPartiallyRefundable;

  return (
    <CustomModal.ButtonRow
      style={{ justifyContent: "center", alignItems: "center" }}
    >
      <div
        style={{
          marginRight: "auto",
          display: "flex",
          alignItems: "center",
        }}
      >
        {maybeRenderRefundActions()}
      </div>
      <Button
        variant="primary"
        disabled={
          (!paymentIsRefundable && !paymentIsPartiallyRefundable) ||
          refundAmountError != null ||
          isRefunding
        }
        onClick={() =>
          onRefund(isPartialRefund ? amountToBeRefunded : undefined)
        }
      >
        {isPartialRefund ? t("REFUND_PARTIAL") : t("REFUND")}
      </Button>
      <Button variant="secondary" onClick={onClose}>
        {t("CLOSE")}
      </Button>
    </CustomModal.ButtonRow>
  );
};

export const RefundDialog = ({ payment }: { payment: Payment }) => {
  const { t } = useTranslation();
  const [, setRefundPaymentIdParam] = useRefundDialogParam();
  const paymentWithFormatted = payment as PaymentWithFormattedData;

  const [isRefunding, setIsRefundig] = useState(false);
  const [error, setError] = useState("");

  const { data: paylogs = [], refetch: refetchPaylogs } = useQuery({
    queryKey: ["paylogs", payment.paymentId],
    queryFn: async () =>
      (await getPaylogs(payment.paymentId)) as PaymentLogItem[],
  });

  const closeDialog = () => {
    setRefundPaymentIdParam(null);
  };

  const handleRefund = async (amount?: number) => {
    try {
      setIsRefundig(true);
      setError("");
      await refundPayment({ paymentId: payment.paymentId, amount });
      await refetchPaylogs();
      closeDialog();
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      setError(e.data.info.message.message);
    } finally {
      setIsRefundig(false);
    }
  };

  return (
    <CustomModal.Wrapper
      open={true}
      onClose={closeDialog}
      titleText={t("REFUND")}
      size="small"
      hasMaxWidth={false}
    >
      {error && (
        <Typography insensitive variant="h3" color="ERROR">
          {error}
        </Typography>
      )}

      <Table>
        {/* eslint-disable i18next/no-literal-string */}
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell align="left">
              PaymentLogID
            </StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">
              Reference
            </StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">Action</StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">Amount</StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">VAT</StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">Currency</StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">Comment</StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">
              Payment ID
            </StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">
              Created at
            </StyledTableHeaderCell>
            <StyledTableHeaderCell align="left">
              Updated at
            </StyledTableHeaderCell>
          </TableRow>
        </TableHead>
        {/* eslint-enable i18next/no-literal-string */}
        <TableBody>
          {paylogs.map((paymentLogItem: PaymentLogItem) => (
            <TableRow key={paymentLogItem.paymentLogId}>
              <StyledTableBodyCell align="left">
                {paymentLogItem.paymentLogId}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.reference}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.action}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.amount}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.vat}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.currency}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.comment}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.paymentId}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.createdAt}
              </StyledTableBodyCell>
              <StyledTableBodyCell align="left">
                {paymentLogItem.updatedAt}
              </StyledTableBodyCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div style={{ marginBottom: "2rem" }} />

      <RefundDialogActions
        isRefunding={isRefunding}
        payment={paymentWithFormatted}
        onClose={closeDialog}
        onRefund={handleRefund}
      />
    </CustomModal.Wrapper>
  );
};

const StyledTableHeaderCell = styled(TableCell)`
  color: ${(p) => defaultTheme.COLORS.GRAY_DARK};
  font-size: 12px;
  padding: 0.5rem;
`;

const StyledTableBodyCell = styled(TableCell)`
  color: ${(p) => defaultTheme.COLORS.GRAY_BLACK};
  font-size: 18px;
  padding: 1rem 0.5rem;
`;
